import {
  Container,
  Text,
  Heading,
  Flex,
  Divider,
  Button,
  Input,
  Grid,
  GridItem,
  Textarea,
  Icon,
  Accordion,
  AccordionItem,
  AccordionButton,
  Box,
  AccordionPanel,
  Select,
  useToast,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Stack,
  HStack,
  VStack,
  InputGroup,
  InputLeftAddon,
  Spinner,
  NumberInput,
  NumberInputField,
  useTheme,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { FiEdit2 } from 'react-icons/fi';
import { BLACK, CONTAINER_MAX_WIDTH_1, CONTAINER_MAX_WIDTH_2 } from 'src/constants/ui';
import React, { useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  BasicCategory,
  MarketplaceAccount,
  Variant,
  ListingImage,
  BasicAttributeValue,
  ShippingInfo,
  ParcelInfo,
  KeysavvyInfo,
  defaultShippingInfo,
  defaultParcelInfo,
  defaultAuctionInfo,
  FulfillmentType,
  FulfillmentOption,
  ExternalListingInfo,
  Currency,
  FlatAttributeValuesType,
  ValueObject,
  AttributeOption,
  Listing,
  AuctionInfo,
  CategoryPreview,
  CategoryPayment,
} from 'src/api/v1-api';
import {
  AddressInput,
  AttributeInput,
  ListingFormSubmit,
  LoginPrompt,
  ErrorMessageBox,
} from 'src/components/atoms';
import { VariantForm, ListingImport } from 'src/components/organisms';
import { apiRequest, handleNextRevalidation } from 'src/utils/fetchUtils';
import { useAuth } from 'src/utils/auth';
import { useRouter } from 'next/router';
import { MarketplaceContext } from 'src/contexts/marketplace';
import {
  CategorySelector,
  ListingLocationForm,
  PriceCard,
  SiblingToUpdateCard,
  ListingImageEditor,
  ISoldThis,
  ParcelInfoForm,
  KeysavvyInfoForm,
} from 'src/components/molecules';
import ListingFeaturedForm from 'src/components/molecules/listing-featured-form';
import NextLink from 'next/link';
import { capitalizeFirstLetter, utcISOToLocalDate } from 'src/utils/common';
import { CATEGORY_TYPE_MARKETPLACE, MINIMUM_PRICE_ALLOWED } from 'src/constants/marketplace';
import { checkAttributeValidity, flattenAttributeValues } from 'src/utils/attributeUtils';
import {
  hasValidPaymentForCategory,
  isListingActiveOrAfter,
  validateAddress,
} from 'src/utils/listingUtils';
import { categoryTypeHasPrice, fetchCategoryPermissionsRoles } from 'src/utils/categoryUtils';
import { parse, format, parseISO } from 'date-fns';
import ListingAuctionForm from 'src/components/molecules/listing-auction-form';
import { isEmpty } from 'lodash';
import AttributeValueCaptureModal from '../attribute-value-capture-modal';
import useRequest from 'src/utils/hooks/useRequest';
import { FEATURE_IDS } from 'src/constants/tier-features';
import { EmailCollector } from 'src/components/molecules';
import { EmailCollectorRef } from 'src/components/molecules/email-collector';
import * as Sentry from '@sentry/nextjs';
import { getListingUrl, getMarketplaceUrl } from 'src/utils/marketplaceUtils';
import useSingleToast from 'src/utils/hooks/toast/useSingleToast';

export type FormInputValues = {
  id: number | null;
  title: string;
  category: BasicCategory | null;
  description: string;
  listing_attribute_values: BasicAttributeValue[];
  listing_images: ListingImage[];
  marketplace_account: MarketplaceAccount | null;
  status: string;
  variants: Variant[];
  shipping_info: ShippingInfo;
  parcel_info: ParcelInfo;
  keysavvy_info: KeysavvyInfo | null;
  listing_fulfillment_options: FulfillmentOption[];
  is_featured: boolean;
  external_url?: string | number | readonly string[] | undefined;
  source?: string | number | readonly string[] | undefined;
  external_listing_info?: ExternalListingInfo;
  currency: Currency;
  listed_at?: string;
  auction_info: AuctionInfo;
  category_payments?: CategoryPayment[];
  sub_status: string | null;
};

export type FormValues = {
  id: number | null;
  title: string;
  category: BasicCategory | null;
  description: string;
  listing_attribute_values: FlatAttributeValuesType;
  listing_images: ListingImage[];
  status: string;
  variants: Variant[];
  shipping_info: ShippingInfo;
  parcel_info: ParcelInfo;
  keysavvy_info: KeysavvyInfo | null;
  is_featured: boolean;
  listed_at: string;
  auction_info: AuctionInfo;
};

export type FlatVariant = {
  id?: number;
  regular_price?: string;
  offered_price?: string;
  is_active?: boolean;
  limited_stock?: boolean;
  quantity?: number;
  variant_attribute_values?: FlatAttributeValuesType;
};

type ListingFormProps = {
  listing: FormInputValues | null;
  writingMode?: 'Add' | 'Edit';
  categoryId?: string;
  updateListing?: (listing: FormInputValues | Listing) => void;
  categoryData?: CategoryPreview;
};

export interface BasicInfoType extends Record<string, string | number | string[] | boolean> {
  title: string;
  description: string;
}

export interface FileWithPosition {
  file: File;
  position: number;
  preview?: string;
}

export interface FilePath {
  filePath?: string;
  position: number;
  value?: string;
}

const IMAGE_FILE_REGEX = /\.(jpe?g|png|gif|bmp|webp|avif)$/i;

const isImageFilePath = (path?: string) => {
  if (!path) return false;
  const [sanitizedPath] = path.split('?');
  return IMAGE_FILE_REGEX.test(sanitizedPath || path);
};

const getFileNameFromPath = (path: string) => {
  try {
    const [cleanPath] = path.split('?');
    const segments = (cleanPath || path).split('/');
    return decodeURIComponent(segments.pop() || cleanPath || path);
  } catch {
    return path;
  }
};

const validateImageUrl = (url: string, timeoutMs = 8000) => {
  if (typeof window === 'undefined') {
    return Promise.resolve(true);
  }

  return new Promise<boolean>((resolve) => {
    let settled = false;
    const img = new window.Image();

    const finalize = (success: boolean) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      img.onload = null;
      img.onerror = null;
      resolve(success);
    };

    const timeoutId = window.setTimeout(() => finalize(false), timeoutMs);

    img.onload = () => finalize(true);
    img.onerror = () => finalize(false);

    const cacheBustedUrl = url.includes('?')
      ? `${url}&cb=${Date.now()}`
      : `${url}?cb=${Date.now()}`;
    img.src = cacheBustedUrl;
  });
};

const AttributesList = React.memo(function AttributesList({
  attributes,
  values,
  onChange,
}: {
  attributes: any[] | undefined;
  values: FlatAttributeValuesType;
  onChange: (id: number | string, value: any) => void;
}) {
  return (
    <>
      {attributes
        ?.filter((attr) => attr.nested_content_type_name == 'Listing')
        ?.filter((attr) => !attr.collect_in_modal_submission)
        ?.sort((a: any, b: any) => a.position - b.position)
        ?.map((attribute: any) => {
          const value = values[attribute.id];
          if (attribute.format !== 'address') {
            return (
              <GridItem
                colSpan={{ base: 2, md: attribute.format === 'media' ? 2 : 1 }}
                key={attribute.id}
              >
                <AttributeInput<BasicInfoType>
                  key={attribute.id}
                  attribute={attribute}
                  width="full"
                  isMobileView
                  value={value}
                  setChange={(e: any) => onChange(attribute.id, e.target.value)}
                  setSelectChange={(e: any) => onChange(attribute.id, e.target.value)}
                  setSwitchChange={(checked: any) => onChange(attribute.id, checked)}
                  setUrlChange={(v: any) => onChange(attribute.id, v)}
                  setMediaChange={(v: any) => onChange(attribute.id, v)}
                  setMultiselectChange={(v: any) => onChange(attribute.id, v)}
                />
              </GridItem>
            );
          }
          return null;
        })}
    </>
  );
});

export const DEFAULT_KEYSAVVY_INFO: KeysavvyInfo = {
  seller_accept: false,
  vin: '',
};

const ListingForm = ({
  listing,
  writingMode = 'Add',
  categoryId,
  updateListing,
  categoryData,
}: ListingFormProps) => {
  const router = useRouter();
  const toast = useToast();
  const showToast = useSingleToast();
  const {
    getToken,
    getOptionalToken,
    isAuthenticated,
    authLoading,
    hasAdmin,
    hasStaff,
    hasRole,
    hasRoles,
    user,
  } = useAuth();
  const { marketplace, hostname } = useContext(MarketplaceContext);
  const toastShownRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isAdmin = hasAdmin(String(marketplace?.id ?? ''));
  const isLomaAdmin = hasRole(String(marketplace?.id ?? ''), 'loma_admin');
  const isStaff = hasStaff();
  const isStaffOrAdmin = isAdmin || isStaff;
  const { api } = useRequest();

  const isSingleListingImportAvailable =
    marketplace?.tier?.features?.[FEATURE_IDS.SINGLE_LISTING_IMPORT] === true;

  useEffect(() => {
    if (router.isReady && !toastShownRef.current) {
      if (router.query.success === 'true') {
        toast({
          title: 'Success',
          description: router.query.message as string,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else if (router.query.success === 'false') {
        toast({
          title: 'Error',
          description: router.query.message as string,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
      toastShownRef.current = true;
    }
  }, [router.isReady, router.query]);

  const [listingId, setListingId] = useState<number | null>(listing?.id ?? null);
  const [email, setEmail] = useState<string>('');
  const [title, setTitle] = useState<string>(listing?.title ?? '');
  const [description, setDescription] = useState<string>(listing?.description ?? '');
  const [selectedCategory, setSelectedCategory] = useState<BasicCategory | null>(
    listing?.category || null,
  );
  const takeRate = selectedCategory?.take_rate ? parseFloat(selectedCategory.take_rate) : 0.08;
  const status = listing?.status ?? 'draft';

  const flattenedListingAttributeValues: FlatAttributeValuesType = useMemo(
    () => flattenAttributeValues(listing?.listing_attribute_values || []),
    [listing?.listing_attribute_values],
  );
  const [listingAttributeValues, setListingAttributeValues] = useState<FlatAttributeValuesType>(
    flattenedListingAttributeValues,
  );

  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [submitInfoLoading, setSubmitInfoLoading] = useState<boolean>(false);
  const [submitDraftLoading, setSubmitDraftLoading] = useState<boolean>(false);
  const isLiveAuction =
    selectedCategory?.type === 'auction' && isListingActiveOrAfter(listing?.status);
  const disallowUserEdits = isLiveAuction && !isStaffOrAdmin;

  const flattenedVariants: FlatVariant[] | undefined = useMemo(
    () =>
      listing?.variants?.map((variant) => {
        let flattenedVariantAttributeValues: FlatAttributeValuesType = {};
        if (variant.variant_attribute_values) {
          flattenedVariantAttributeValues = flattenAttributeValues(
            variant.variant_attribute_values,
          );
        }
        return { ...variant, variant_attribute_values: flattenedVariantAttributeValues };
      }),
    [listing?.variants],
  );

  const [variants, setVariants] = useState<FlatVariant[]>(flattenedVariants || []);
  const [currentVariant, setCurrentVariant] = useState<FlatVariant | null>(null);
  // Check if the selected category has variant attributes to determine if the listing can be multi-variant
  const isMultiVariant = useMemo(
    () =>
      !!(
        selectedCategory?.multiple_variants &&
        selectedCategory.attributes.filter((attr) => attr.nested_content_type_name == 'Variant')
          .length > 0
      ),
    [selectedCategory],
  );
  if (!isMultiVariant && variants.length > 0 && !currentVariant) {
    setCurrentVariant(variants[0]);
  }

  const [shippingInfo, setShippingInfo] = useState<ShippingInfo | null>(
    listing?.shipping_info || defaultShippingInfo,
  );
  const [parcelInfo, setParcelInfo] = useState<ParcelInfo>(
    listing?.parcel_info || defaultParcelInfo,
  );
  const [keysavvyInfo, setKeysavvyInfo] = useState<KeysavvyInfo | null>(
    listing?.keysavvy_info || null,
  );
  const locationAttribute = listing?.listing_attribute_values?.filter(
    (attr) => attr.attribute.name == 'Location',
  );
  const [location, setLocation] = useState<ShippingInfo | null>(
    locationAttribute && locationAttribute.length > 0
      ? (locationAttribute[0].value_object as ShippingInfo)
      : null,
  );
  const [hasLocation, setHasLocation] = useState<boolean>(!!location);
  const [sameAsShippingInfo, setSameAsShippingInfo] = useState<boolean>(false);

  const [filePaths, setFilePaths] = useState<FilePath[]>([]);
  const [imageUploading, setImageUploading] = useState(false);
  const [selectedChild, setSelectedChild] = useState<BasicCategory | null>(null);
  const [isPayToList, setIsPayToList] = useState<boolean>(false);
  const [payToListChildren, setPayToListChildren] = useState<BasicCategory[]>([]);
  const [payToListParent, setPayToListParent] = useState<BasicCategory | null>(null);
  const [parentCategory, setParentCategory] = useState<BasicCategory | null>(null);
  const [isFeatured, setIsFeatured] = useState(listing?.is_featured ?? false);
  const [externalUrl, setExternalUrl] = useState<string | number | readonly string[] | undefined>(
    listing?.external_url,
  );
  const [source, setSource] = useState<string | number | readonly string[] | undefined>(
    listing?.source,
  );
  const normalizedSource = useMemo(() => {
    if (!source) return '';
    if (Array.isArray(source)) {
      return source[0] ?? '';
    }
    return String(source);
  }, [source]);
  const isFirstPartyListing = useMemo(() => {
    if (!normalizedSource) return true;
    return normalizedSource.trim().toLowerCase() === 'loma';
  }, [normalizedSource]);
  const [listerHandle, setListerHandle] = useState<string | number | readonly string[] | undefined>(
    listing?.external_listing_info?.lister_handle,
  );
  const [currency, setCurrency] = useState<string>(listing?.currency ?? Currency.USD);
  const [hasPermission, setHasPermission] = useState<boolean>(true);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [listedAt, setListedAt] = useState<string>('');
  const [auctionInfo, setAuctionInfo] = useState<AuctionInfo>(
    listing?.auction_info || defaultAuctionInfo,
  );
  const theme = useTheme();
  const now = new Date();
  const auctionEnd =
    !!listing?.auction_info?.id && listing?.auction_info?.end
      ? utcISOToLocalDate(listing.auction_info.end)
      : null;
  const isNoAuctionOrEnded =
    !listing?.auction_info?.id ||
    (listing?.auction_info?.status === 'admin_approved' && auctionEnd && now > auctionEnd);
  const [defaultOverrides, setDefaultOverrides] = useState({} as Record<string, string>);
  const [showAttributeCaptureModal, setShowAttributeCaptureModal] = useState<boolean>(false);
  const emailCollectorRef = useRef<EmailCollectorRef>(null);

  useEffect(() => {
    const overdefs = {} as Record<string, string>;
    const categoryOverrides = selectedCategory?.functional?.text_overrides || {};
    // category default override the global defaults
    for (const textover of marketplace?.text_overrides ?? []) {
      overdefs[textover.name] = textover.default_value;
      if (textover.config_type === 'category' && (categoryOverrides[textover.name] ?? '')) {
        overdefs[textover.name] = categoryOverrides[textover.name];
      }
    }
    setDefaultOverrides(overdefs);
  }, [marketplace?.text_overrides, selectedCategory?.functional?.text_overrides]);

  useEffect(() => {
    if (listing?.listing_images) {
      // no CDN here, likely unwanted
      const filePaths = listing?.listing_images
        .map((image) => ({
          filePath: image.image,
          position: image.position,
        }))
        .sort((a, b) => a.position - b.position);
      setFilePaths(filePaths);
    }
  }, [listing?.listing_images]);

  useEffect(() => {
    try {
      if (listing?.listed_at) {
        const date = parseISO(listing?.listed_at);
        setListedAt(format(date, 'M/d/yyyy, h:mm:ss a'));
      }
    } catch (error) {
      console.error(error);
    }
  }, [listing?.listed_at]);

  const formatDate = () => {
    try {
      const inputFormat = 'M/d/yyyy, h:mm:ss a';
      const parsedDate = parse(listedAt, inputFormat, new Date());
      const isoFormatDate = format(parsedDate, "yyyy-MM-dd'T'HH:mm:ss.SSSXXX");
      return isoFormatDate;
    } catch (error) {
      return '';
    }
  };

  const currentCategoryId = selectedCategory?.id
    ? selectedCategory.id
    : parseInt(String(categoryId ?? ''), 10);

  const handleAttributeChange = useCallback(
    (
      attributeId: number | string,
      value: string | number | boolean | ValueObject | FilePath[] | AttributeOption[],
    ) => {
      setListingAttributeValues((prevValues) => ({ ...prevValues, [attributeId]: value }));
    },
    [],
  );

  const isValueInvalid = (
    value:
      | string
      | number
      | boolean
      | FilePath
      | FilePath[]
      | ValueObject
      | ValueObject[]
      | undefined,
  ) => {
    if (value === undefined) return true;
    if (Array.isArray(value) && value.length == 0) return true;
    if (value === '') return true;
    return false;
  };

  const validateVariant = useCallback(() => {
    if (!currentVariant) return false;

    if (!currentVariant.quantity || currentVariant.quantity < 0) {
      // default to quantity of 1 if none entered
      currentVariant.quantity = 1;
    }

    if (
      !currentVariant.offered_price ||
      parseFloat(currentVariant.offered_price) < MINIMUM_PRICE_ALLOWED
    ) {
      toast({
        title: `Variant offered price must be more than \$${MINIMUM_PRICE_ALLOWED}.`,
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
      return false;
    }

    const variantAttributes =
      selectedCategory?.attributes
        ?.filter((attr) => attr.nested_content_type_name === 'Variant')
        ?.sort((a, b) => a.position - b.position) ?? [];
    for (const variantAttribute of variantAttributes) {
      const attributeValue = currentVariant?.variant_attribute_values?.[variantAttribute.id];
      if (variantAttribute.required && isValueInvalid(attributeValue)) {
        toast({
          title: `"${variantAttribute.name}" must have a value.`,
          status: 'warning',
          duration: 5000,
          isClosable: true,
          position: 'bottom',
        });
        return false;
      }
    }
    return true;
  }, [currentVariant, selectedCategory, toast]);

  const handleAddVariantSubmit = useCallback(() => {
    if (validateVariant()) {
      setVariants((prevValues) => [
        ...prevValues,
        { ...currentVariant, id: prevValues.length + 1 },
      ]);
      setCurrentVariant(null);
    }
  }, [validateVariant, currentVariant]);

  const handleEditVariantSubmit = useCallback(
    (id: number | undefined) => {
      if (!id) return;
      if (validateVariant()) {
        setVariants((prevValues) => [
          ...prevValues.filter((val) => val.id != id),
          { ...currentVariant, id },
        ]);
      }
    },
    [validateVariant, currentVariant],
  );

  const deleteVariant = useCallback(() => {
    if (!currentVariant?.id) return;
    setVariants((prevValues) => prevValues.filter((val) => val.id != currentVariant?.id));
    setCurrentVariant(null);
  }, [currentVariant]);

  const encodeAuctionInfo = (auctionInfo: AuctionInfo) => {
    auctionInfo.startLocal = undefined;
    auctionInfo.endLocal = undefined;
    return JSON.stringify(auctionInfo);
  };

  const showSubmitError = (errorMessage: string, errorObj?: any, extraContext?: object) => {
    showToast({
      title: 'Error.',
      description: errorMessage || 'Something went wrong. Please try again.',
      status: 'error',
      duration: 9000,
      isClosable: true,
      position: 'bottom',
      sentryAlert: {
        error: errorObj instanceof Error ? errorObj : new Error(errorMessage),
        level: 'error',
        tags: {
          component: 'ListingForm',
          feature: 'listing_creation',
          action: 'submit_listing',
          marketplace_id: marketplace?.id?.toString(),
          category_id: selectedCategory?.id?.toString(),
          category_type: selectedCategory?.type,
          listing_id: listingId?.toString(),
          writing_mode: writingMode,
        },
        extras: {
          errorMessage,
          listingTitle: title,
          categoryName: selectedCategory?.name,
          hasImages: filePaths.length > 0,
          imageCount: filePaths.length,
          variantCount: variants.length,
          isAuthenticated: !!user,
          ...extraContext,
        },
      },
    });
    setSubmitLoading(false);
  };

  const checkFormValidity = async () => {
    let isValid = true;
    let warningMessage = '';
    let addressErrorMessage = '';
    if (!selectedCategory) {
      warningMessage = 'Please select a category.';
      isValid = false;
    }
    const error_message = checkAttributeValidity(
      selectedCategory?.attributes,
      listingAttributeValues,
    );
    if (error_message !== '') {
      warningMessage = error_message;
      isValid = false;
    }

    if (!description) {
      warningMessage =
        'Please enter ' + (defaultOverrides['listing_description'] ?? 'a description') + '.';
      isValid = false;
    }

    if (selectedCategory && categoryTypeHasPrice(selectedCategory.type) && variants.length === 0) {
      if (!currentVariant) {
        warningMessage = 'Please add a price.';
        isValid = false;
      } else if (!validateVariant()) {
        return false;
      }
    }

    if (
      selectedCategory &&
      categoryTypeHasPrice(selectedCategory.type) &&
      parseFloat(currentVariant?.offered_price ?? '0') <= 0
    ) {
      warningMessage = 'Offered price must be positive.';
      isValid = false;
    }

    if (fulfillmentPrice && parseFloat(fulfillmentPrice) < 0) {
      warningMessage = 'Shipping price must be positive.';
      isValid = false;
    }

    if (!selectedFulfillment && selectedCategory?.type === CATEGORY_TYPE_MARKETPLACE) {
      // Skip the fulfillment check for external listings
      if (!externalUrl) {
        warningMessage = 'Please select a fulfillment option.';
        isValid = false;
      }
    }

    if (
      selectedFulfillment &&
      selectedFulfillment?.price_type === 'dynamic_api' &&
      addressErrorMessage === '' &&
      !externalUrl
    ) {
      let hasAllAddressInfo = true;
      if (
        !shippingInfo?.name ||
        !shippingInfo?.phone ||
        !shippingInfo?.street_address1 ||
        !shippingInfo?.city ||
        !shippingInfo?.state ||
        !shippingInfo?.zip_code ||
        !shippingInfo?.country
      ) {
        warningMessage = 'Please enter all shipping info.';
        hasAllAddressInfo = false;
        isValid = false;
      }

      if (hasAllAddressInfo) {
        addressErrorMessage = await validateAddress(shippingInfo);

        if (addressErrorMessage !== '') {
          isValid = false;
          warningMessage = addressErrorMessage;
        }
      }

      if (
        !parcelInfo ||
        parcelInfo.length <= 0.0 ||
        parcelInfo.width <= 0.0 ||
        parcelInfo.height <= 0.0 ||
        parcelInfo.weight <= 0.0
      ) {
        warningMessage = 'Please enter valid parcel info.';
        isValid = false;
      }
    }
    if (
      selectedFulfillment &&
      selectedFulfillment?.name === 'KeySavvy' &&
      !keysavvyInfo?.vin?.trim()
    ) {
      warningMessage = 'Please enter a valid VIN.';
      isValid = false;
    }
    if (
      selectedFulfillment &&
      selectedFulfillment?.name === 'KeySavvy' &&
      !keysavvyInfo?.seller_accept
    ) {
      warningMessage = 'You must accept the KeySavvy Terms of Service.';
      isValid = false;
    }
    if (
      isLocationRequired &&
      ((location?.city ?? '').trim() === '' || (location?.state ?? '').trim() === '') &&
      locationAttribute &&
      locationAttribute.length > 0
    ) {
      warningMessage = 'Please enter city and state.';
      isValid = false;
    }

    if (
      isAuthenticated &&
      !paymentInfoFound &&
      selectedCategory?.type === CATEGORY_TYPE_MARKETPLACE &&
      !externalUrl
    ) {
      warningMessage =
        'Payment information not found. Please go to your account information page to fill out payout information.';
      isValid = false;
    }

    if (
      filePaths.length === 0 &&
      (selectedCategory?.functional?.listing_images_required ||
        selectedCategory?.functional?.requires_payment ||
        selectedCategory?.has_promo)
    ) {
      isValid = false;
      warningMessage = 'Please upload at least one image or video in order to publish your listing';
    }

    if (listedAt !== '' && formatDate() === '') {
      isValid = false;
      warningMessage = 'Please use a valid Listed Date Time';
    }

    if (
      auctionInfo &&
      auctionInfo.start &&
      auctionInfo.end &&
      new Date(auctionInfo.start) >= new Date(auctionInfo.end)
    ) {
      isValid = false;
      warningMessage = `Auction start date must be before end date.`;
    }

    // Check for required media attributes
    const requiredMediaAttributes = selectedCategory?.attributes?.filter(
      (attr) => attr.format === 'media' && attr.required && !attr.collect_in_modal_submission,
    );

    for (const attr of requiredMediaAttributes || []) {
      const mediaValues = listingAttributeValues[attr.id] || [];
      const fileCount = Array.isArray(mediaValues) ? mediaValues.length : 0;

      if (fileCount === 0) {
        isValid = false;
        warningMessage = `Please upload at least one piece of media for ${attr.name}.`;
        break;
      }

      if (attr.min_value && fileCount < Number(attr.min_value)) {
        isValid = false;
        warningMessage = `Please upload at least ${attr.min_value} files for ${attr.name}.`;
        break;
      }

      if (attr.max_value && fileCount > Number(attr.min_value)) {
        isValid = false;
        warningMessage = `Please upload no more than ${attr.max_value} files for ${attr.name}.`;
        break;
      }
    }

    if (!isValid) {
      toast({
        title: warningMessage,
        status: 'warning',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    }

    return isValid;
  };

  const determineListingStatus = (action: string, status: string | undefined) => {
    let listingStatus = null;
    if (action === 'submitDraft') {
      listingStatus = 'draft';
    } else if (action === 'publish') {
      if (
        selectedCategory?.functional?.requires_payment &&
        selectedCategory?.functional?.requires_approval
      ) {
        // if approval and payment needed, approval will happen first
        if (status !== 'active' && status !== 'pending_payment') {
          listingStatus = 'pending';
        } else if (status !== 'active') {
          listingStatus = 'pending_payment';
        }
        // Check if the category requires payment
      } else if (selectedCategory?.functional?.requires_payment) {
        const hasValidPayment = hasValidPaymentForCategory(
          listing?.category_payments,
          selectedCategory?.id,
        );
        if (hasValidPayment) {
          listingStatus = 'active';
        } else {
          listingStatus = 'pending_payment';
        }
        // Check if the category requires approval
      } else if (selectedCategory?.functional?.requires_approval && status !== 'active') {
        listingStatus = 'pending';
        // Set the status to pending by default for Loma Admins, keep current status when editing
      } else if (status === 'candidate') {
        listingStatus = 'candidate';
      } else if (isLomaAdmin) {
        if (!(status === 'active' || status === 'pending')) {
          listingStatus = 'pending';
        }
      } else {
        listingStatus = 'active';
      }
    }
    return listingStatus;
  };

  const attributeIdsForCaptureModal =
    selectedCategory?.attributes
      ?.filter((attr) => attr.nested_content_type_name == 'Listing')
      ?.filter((attr) => attr.collect_in_modal_submission)
      ?.sort((a, b) => a.position - b.position)
      ?.map((attribute) => attribute.id) || [];

  const handleSubmit = async (e?: any) => {
    e?.preventDefault();

    if (emailCollectorRef.current && emailCollectorRef.current.isExistingEmail()) {
      return emailCollectorRef.current.openModal();
    }

    setShowAttributeCaptureModal(false);
    // submitting the form will have the action in the first location, saving draft in the other
    const action =
      e?.nativeEvent?.submitter?.dataset?.action ?? e?.target.dataset.action ?? 'publish';
    if (action === 'publish') {
      setSubmitLoading(true);
      const validForm = await checkFormValidity();
      if (!validForm) {
        setSubmitLoading(false);
        return;
      }

      if (
        attributeIdsForCaptureModal.length > 0 &&
        selectedCategory?.attributes.some(
          (attr) =>
            attributeIdsForCaptureModal.includes(attr.id) &&
            attr.required &&
            !listingAttributeValues[attr.id],
        )
      ) {
        setSubmitLoading(false);
        setShowAttributeCaptureModal(true);
        return;
      }
    } else if (action === 'submitDraft') {
      setSubmitDraftLoading(true);
    } else {
      setSubmitInfoLoading(true);
      if (['active', 'pending', 'pending_payment', 'candidate'].includes(listing?.status ?? '')) {
        const validForm = await checkFormValidity();
        if (!validForm) {
          setSubmitInfoLoading(false);
          return;
        }
      }
    }

    let submitVariants = [];
    if (!isMultiVariant && currentVariant) {
      submitVariants = [currentVariant];
    } else {
      submitVariants = variants;
    }

    const formData = new FormData();

    if (
      !isAuthenticated &&
      writingMode === 'Add' &&
      emailCollectorRef.current &&
      !emailCollectorRef.current.isExistingEmail()
    ) {
      formData.append('email', email);
    }

    if (listingId) {
      formData.append('id', listingId.toString());
    }
    if (selectedCategory) {
      formData.append('category', selectedCategory.id.toString());
    }
    formData.append('title', title);
    formData.append('description', description);
    formData.append('listing_attribute_values', JSON.stringify(listingAttributeValues));
    formData.append('currency', currency);

    if (variants.length === 0 && currentVariant) {
      formData.append('variants', JSON.stringify([currentVariant]));
    } else if (variants.length > 0) {
      formData.append('variants', JSON.stringify(submitVariants));
    }

    formData.append('shipping_info', JSON.stringify(shippingInfo));
    formData.append('parcel_info', JSON.stringify(parcelInfo));
    if (hasLocation || isLocationRequired) {
      if (sameAsShippingInfo) {
        formData.append('location', JSON.stringify(shippingInfo));
      } else {
        formData.append('location', JSON.stringify(location));
      }
    } else {
      formData.append('location', JSON.stringify({}));
    }
    if (selectedFulfillment?.name === 'KeySavvy' && keysavvyInfo) {
      formData.append('keysavvy_info', JSON.stringify(keysavvyInfo));
    }

    if (selectedFulfillment) {
      formData.append('selected_fulfillment_type', JSON.stringify(selectedFulfillment));
      formData.append('fulfillment_price', fulfillmentPrice || '');
    }

    const listingStatus = determineListingStatus(action, listing?.status);

    if (
      isFirstPartyListing &&
      filePaths.some((filePathObj) => isImageFilePath(filePathObj.filePath))
    ) {
      const imageUrls = filePaths
        .map((filePathObj) => filePathObj.filePath)
        .filter((url): url is string => Boolean(url))
        .filter((url) => isImageFilePath(url));

      // Validate for now just the first image to avoid long waits
      const imageUrlsToValidate = imageUrls.slice(0, 1);
      const validationResults = await Promise.all(
        imageUrlsToValidate.map((url) => validateImageUrl(url)),
      );
      const failedImages = imageUrlsToValidate.filter((_, idx) => !validationResults[idx]);

      if (failedImages.length > 0) {
        const formattedList = failedImages.map((url) => getFileNameFromPath(url)).join(', ');
        toast({
          title:
            failedImages.length === 1 ? 'Image could not be loaded' : 'Images could not be loaded',
          description:
            failedImages.length === 1
              ? `${formattedList} could not be downloaded. Please remove or re-upload it before saving the listing.`
              : `These files could not be downloaded: ${formattedList}. Remove or re-upload them before saving the listing.`,
          status: 'error',
          duration: 8000,
          isClosable: true,
          position: 'bottom',
        });
        Sentry.captureMessage('listing_image_validation_failed', {
          level: 'warning',
          extra: {
            listingId: listingId ?? 'new',
            failedImages,
          },
        });
        setSubmitLoading(false);
        setSubmitInfoLoading(false);
        setSubmitDraftLoading(false);
        return;
      }
    }

    if (listingStatus) {
      formData.append('status', listingStatus);
    } else {
      formData.append('status', status);
    }

    if (isStaffOrAdmin) {
      formData.append('is_featured', String(isFeatured));
    }

    // Admin fields
    if (isLomaAdmin || isStaffOrAdmin) {
      formData.append('external_url', String(externalUrl ?? ''));
      formData.append('source', String(source ?? ''));
      formData.append(
        'external_listing_info',
        JSON.stringify({ lister_handle: listerHandle ?? '' }),
      );
    }

    if (listedAt !== '') {
      formData.append('listed_at', formatDate());
    }

    if (filePaths.length > 0) {
      formData.append('file_paths', JSON.stringify(filePaths));
    }
    if (selectedCategory?.type === 'auction') {
      formData.append('auction_info', encodeAuctionInfo(auctionInfo));
    }

    let response;
    if (!listingId) {
      const token = await getOptionalToken();
      const url = `${process.env.NEXT_PUBLIC_API_HOST}/listings/create/`;
      try {
        response = await apiRequest('POST', url, token, formData, true);
        setListingId(response.id);

        // Delete local storage data
        const key = buildLocalStorageKey();
        if (key) {
          localStorage.removeItem(key);
        }
      } catch (err: any) {
        showSubmitError(err.message, err, { operation: 'create_listing' });
        setSubmitLoading(false);
        setSubmitDraftLoading(false);
        setSubmitInfoLoading(false);
        return;
      }
    } else {
      const token = await getToken();
      const url = `${process.env.NEXT_PUBLIC_API_HOST}/listings/${listingId}/${marketplace?.id}/update/`;
      try {
        response = await apiRequest('PUT', url, token, formData, true);

        const key = buildLocalStorageKey();
        if (key) {
          localStorage.removeItem(key);
        }
      } catch (err: any) {
        console.error(err.message);
        showSubmitError(err.message, err, { operation: 'update_listing' });
        setSubmitLoading(false);
        setSubmitDraftLoading(false);
        setSubmitInfoLoading(false);
        return;
      }
    }

    if (listingId) {
      try {
        // We need to revalidate the listing page with Next after a listing is edited
        await handleNextRevalidation(`/_sites/${hostname}/listing/${listingId}`);
      } catch (error) {
        console.error('Error during revalidation:', error);
      }
    }

    if (action === 'saveInfo') {
      toast({
        title: 'Info Saved',
        description: `Successfully saved listing info.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
      setSubmitInfoLoading(false);
    } else {
      if (!isAuthenticated) {
        const queryParams = new URLSearchParams({
          messageTitle: 'Your draft listing has been created',
          message:
            'Check your email for a link to finish setting up your account. Once your account is ready, you can publish your listing.',
          success: 'true',
        });
        window.location.href = `/?${queryParams.toString()}`;
      } else {
        const routerListingId = listingId ? listingId : response.id;
        const forceDraft = response && response.force_draft ? '?forceDraft=true' : '';
        // Use 'window' instead of router to force a page refresh after Next revalidation
        window.location.href = `/listing/${routerListingId}${forceDraft}`;
      }
    }
  };

  const onCancelSubscription = async () => {
    if (!listingId || !marketplace?.id || !updateListing) return;

    try {
      await api.marketplace.listings.cancelSubscription(listingId, marketplace.id);
      toast({
        title: 'Subscription Cancelled',
        description: 'It will remain active until the end of the billing cycle.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      updateListing({ ...listing, sub_status: 'cancellation_requested' } as FormInputValues);
    } catch {
      toast({
        title: 'Error',
        description: 'There was an error cancelling the subscription. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const [selectedFulfillment, setSelectedFulfillment] = useState<FulfillmentType | null>(
    listing?.listing_fulfillment_options?.length &&
      listing?.listing_fulfillment_options?.length > 0 &&
      listing?.listing_fulfillment_options[0]?.fulfillment_type
      ? listing?.listing_fulfillment_options[0]?.fulfillment_type
      : null,
  );
  const [fulfillmentPrice, setFulfillmentPrice] = useState<string | undefined>(
    listing?.listing_fulfillment_options?.length &&
      listing?.listing_fulfillment_options?.length > 0 &&
      listing?.listing_fulfillment_options[0]?.price
      ? listing?.listing_fulfillment_options[0]?.price
      : undefined,
  );

  const selectedPayToListFormat =
    selectedCategory?.functional?.create_listing_category_select_format;
  const categorySelectorDisabled =
    selectedPayToListFormat === 'pay_to_list_parent' ||
    selectedPayToListFormat === 'pay_to_list_child'
      ? true
      : false;
  const addressAttribute = selectedCategory?.attributes?.find(
    (attribute) => attribute.format === 'address',
  );
  const isLocationRequired = addressAttribute?.required;

  const flagIneligibleCategories = (categories: BasicCategory[]): BasicCategory[] => {
    if (writingMode === 'Add') {
      return categories.map((category) => ({
        ...category,
        isDisabled: !category.functional?.can_create_listings,
        isVisible:
          category.functional?.create_listing_category_select_format !== 'pay_to_list_parent' &&
          category.functional?.create_listing_category_select_format !== 'pay_to_list_child',
      }));
    }
    return categories;
  };

  const [renderState, setRenderState] = useState<'loading' | 'form'>('loading');

  useEffect(() => {
    const fetchCategoryInformation = async (marketplaceId: number) => {
      // If categoryData is provided, use it directly
      if (categoryData) {
        const foundCategory = categoryData;
        setIsPayToList(false);
        setSelectedCategory(foundCategory as BasicCategory);

        setRenderState('form');
        return;
      }

      // Original category fetching logic if no categoryData is provided
      // TODO: returns CategoryInList[] not BasicCategory[]
      const url = `${process.env.NEXT_PUBLIC_API_HOST}/marketplaces/${marketplaceId}/categories/`;
      try {
        const response = await apiRequest('GET', url, null);
        const categoryArray = flagIneligibleCategories(response);
        if (currentCategoryId && categoryArray.length > 0) {
          const foundCategory = categoryArray.find((category) => category.id == currentCategoryId);
          const select_format = foundCategory?.functional?.create_listing_category_select_format;
          const isPTL =
            select_format === 'pay_to_list_parent' || select_format === 'pay_to_list_child';
          setIsPayToList(isPTL ?? false);
          let children: BasicCategory[] = [];

          if (!isPTL) {
            const parentId = foundCategory?.parent;
            const categoryParent = categoryArray.find(
              (category) => category.id == parseInt(String(parentId ?? ''), 10),
            );
            setParentCategory(categoryParent || null);
          }

          if (
            foundCategory &&
            select_format === 'pay_to_list_parent' &&
            !foundCategory?.children?.length
          ) {
            children = categoryArray.filter((cat) => cat.parent === currentCategoryId);
            foundCategory.children = children;
          } else if (foundCategory && select_format === 'pay_to_list_child') {
            const parent = foundCategory.parent;
            children = categoryArray.filter(
              (cat) => cat.parent === parseInt(String(parent ?? ''), 10),
            );
          }
          // Only show child categories that are pay_to_list_child
          children = children.filter(
            (child) =>
              child.functional?.create_listing_category_select_format === 'pay_to_list_child',
          );

          if (select_format === 'pay_to_list_child') {
            const parentId = foundCategory?.parent;
            const categoryParent = categoryArray.find(
              (category) => category.id == parseInt(String(parentId ?? ''), 10),
            );
            setPayToListChildren(children || []);
            setPayToListParent(categoryParent || null);
            setParentCategory(categoryParent || null);
            setSelectedChild(foundCategory || null);
            setSelectedCategory(foundCategory || null);
          } else if (select_format === 'pay_to_list_parent') {
            setPayToListParent && setPayToListParent(foundCategory || null);
            setParentCategory(foundCategory || null);
            setPayToListChildren && setPayToListChildren(children || []);
            if (foundCategory?.children && foundCategory.children.length === 1) {
              setSelectedChild && setSelectedChild(foundCategory.children[0]);
            }
          }
          setSelectedCategory(foundCategory || null);
        }
        setRenderState('form');
      } catch (error) {
        toast({
          title: 'Error fetching categories',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        setRenderState('form');
      }
    };

    if (router.isReady) {
      if (marketplace?.id) {
        fetchCategoryInformation(marketplace?.id);
      }
    }
  }, [categoryId, router.isReady, marketplace, categoryData]);

  useEffect(() => {
    if (marketplace && user && currentCategoryId) {
      checkCategoryPermissions(String(currentCategoryId ?? ''), String(marketplace?.id ?? ''));
    }
  }, [currentCategoryId, marketplace, user]);

  const checkCategoryPermissions = async (catId: string, marketplaceId: string) => {
    const categoryRoles = await fetchCategoryPermissionsRoles(catId);
    // Filter roles to only include those that have "can_create_listings" permission as true
    const canCreateListingRoles = categoryRoles.filter((role) => role.can_create_listing);
    const canCreateListingRoleNames = canCreateListingRoles.map((role) => role.role.name);

    let rolesString = '';
    if (hasRoles(marketplaceId, canCreateListingRoleNames)) {
      setHasPermission(true);
    } else {
      if (canCreateListingRoles.length > 1) {
        rolesString = `The Roles [${canCreateListingRoles
          .map((role) => role.role.name)
          .join(', ')}] are required to create listings in this category.`;
      } else if (canCreateListingRoles.length === 1) {
        rolesString = `The Role "${canCreateListingRoles[0].role.name}" is required to create listings in this category.`;
      } else {
        rolesString = `An Admin role is required to create listings in this category.`;
      }
      setErrorMessage(rolesString);
      setHasPermission(false);
    }
  };

  const handleSelectedChild = (child: any) => {
    setSelectedChild(child);
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const isPayToListChild =
    selectedCategory?.functional?.create_listing_category_select_format === 'pay_to_list_child';
  const siblingsToUpdate = isPayToListChild
    ? payToListChildren.filter((cat) => {
        const catPrice = cat.functional?.pay_to_list?.pricing_card?.price || 0;
        const selCatPrice = selectedCategory?.functional?.pay_to_list?.pricing_card?.price || 0;
        return Number(catPrice) > Number(selCatPrice) && Number(catPrice) > 0;
      })
    : [];
  const inDraftEditingMode = listing?.status === 'draft' && writingMode === 'Edit';
  const saveInfoButtonText = inDraftEditingMode || !listingId ? 'Save Draft' : 'Save Info';

  const [paymentInfoFound, setPaymentInfoFound] = useState<boolean>(false);
  const [fetchingPaymentInfo, setFetchingPaymentInfo] = useState<boolean>(false);
  const [showPayoutCaptureModal, setShowPayoutCaptureModal] = useState<boolean>(false);
  const [showPayoutCaptureModalAttributeIds, setShowPayoutCaptureModalAttributeIds] = useState<
    number[]
  >([]);

  const checkPaymentInfo = useCallback(async () => {
    setShowPayoutCaptureModal(false);
    // Early return if prerequisites aren't met
    if (!marketplace?.id || !isAuthenticated || authLoading || fetchingPaymentInfo) return;

    // Early return if not a marketplace category
    if (selectedCategory?.type !== CATEGORY_TYPE_MARKETPLACE) {
      setPaymentInfoFound(false);
      return;
    }

    setFetchingPaymentInfo(true);

    try {
      const token = await getToken();
      const url = `${process.env.NEXT_PUBLIC_API_HOST}/payout_information_state/${marketplace.id}`;
      const response = await apiRequest('GET', url, token);

      const hasPayoutInfo = response.payout_information_exists;
      setPaymentInfoFound(hasPayoutInfo);

      // Show payment info modal if needed
      if (!hasPayoutInfo && !externalUrl) {
        setShowPayoutCaptureModalAttributeIds(response.attribute_ids);
        setShowPayoutCaptureModal(true);
      }
    } catch (error) {
      console.error('Error fetching payment info:', error);
    } finally {
      setFetchingPaymentInfo(false);
    }
  }, [
    marketplace?.id,
    selectedCategory?.type,
    isAuthenticated,
    authLoading,
    externalUrl,
    getToken,
  ]);

  useEffect(() => {
    if (marketplace && isAuthenticated && !authLoading) {
      checkPaymentInfo();
    }
  }, [marketplace, checkPaymentInfo, authLoading, isAuthenticated]);

  const resetCaptureModaltAttributeValues = (values: FlatAttributeValuesType) => {
    const newValues = Object.keys(listingAttributeValues).reduce((acc, id) => {
      if (attributeIdsForCaptureModal.includes(Number(id))) {
        if (values[id]) {
          acc[id] = values[id];
        }
      } else {
        acc[id] = listingAttributeValues[id];
      }
      return acc;
    }, {} as FlatAttributeValuesType);
    setListingAttributeValues(newValues);
  };

  const renderCustomContent = () => {
    const baseCategory = selectedCategory?.functional?.new_listing_custom_content_use_parent
      ? parentCategory
      : selectedCategory;

    if (
      !baseCategory ||
      !baseCategory.functional?.new_listing_custom_content_title ||
      !baseCategory.functional?.new_listing_custom_content_body ||
      baseCategory.functional?.new_listing_custom_content_body === '' ||
      baseCategory.functional?.new_listing_custom_content_body === '<p><br></p>'
    ) {
      return null;
    }

    return (
      <Flex
        width="full"
        direction="column"
        p={{ base: 2, md: 4, lg: 8 }}
        border="1px"
        borderColor="gray.200"
        borderRadius={theme.border_radius.border_radius_2}
        mb={3}
        className="custom-content"
      >
        <Text fontSize="md" fontWeight={600}>
          {baseCategory?.functional?.new_listing_custom_content_title}
        </Text>
        <span
          className="rich-text-editor-container"
          dangerouslySetInnerHTML={{
            __html: baseCategory?.functional?.new_listing_custom_content_body ?? '',
          }}
        />
      </Flex>
    );
  };

  const renderPayToListCustomContent = () => {
    if (
      payToListParent?.functional?.pay_to_list_custom_content === '' ||
      payToListParent?.functional?.pay_to_list_custom_content === '<p><br></p>'
    ) {
      return null;
    }

    return (
      <Flex
        width="full"
        direction="column"
        p={{ base: 2, md: 4, lg: 8 }}
        mb={3}
        className="custom-content"
      >
        <span
          className="rich-text-editor-container"
          dangerouslySetInnerHTML={{
            __html: payToListParent?.functional?.pay_to_list_custom_content ?? '',
          }}
        />
      </Flex>
    );
  };

  const [localStorageKey, setLocalStorageKey] = useState<string | null>(null);
  const [showLoadDataConfirmBanner, setShowLoadDataConfirmBanner] = useState<boolean>(false);
  const LISTING_DRAFT_EXPIRATION_MINUTES = 10_080; // 7 days

  const buildLocalStorageKey = () => {
    if (!marketplace || !selectedCategory) {
      return '';
    }
    return `${marketplace.id}-${selectedCategory.id}-listing-draft`;
  };

  const dumpListingDataInLocalStorage = (key: string) => {
    const expirationTime = new Date().getTime() + LISTING_DRAFT_EXPIRATION_MINUTES * 60 * 1000;
    const listingData = {
      title,
      description,
      listingAttributeValues,
      currency,
      variants,
      currentVariant,
      shippingInfo,
      parcelInfo,
      keysavvyInfo,
      location,
      selectedFulfillment,
      listerHandle,
      externalUrl,
      source,
      filePaths,
      auctionInfo,
      expirationTime,
      listedAt,
    };
    try {
      localStorage.setItem(key, JSON.stringify(listingData));
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error saving listing data to local storage:', error);
    }
  };

  const loadListingDataFromLocalStorage = (key: string) => {
    const storedData = localStorage.getItem(key);
    if (storedData) {
      try {
        const listingData = JSON.parse(storedData);
        setTitle(listingData.title);
        setDescription(listingData.description);
        setListingAttributeValues(listingData.listingAttributeValues);
        setCurrency(listingData.currency);
        setVariants(listingData.variants);
        setCurrentVariant(listingData.currentVariant);
        setShippingInfo(listingData.shippingInfo);
        setParcelInfo(listingData.parcelInfo);
        setKeysavvyInfo(listingData.keysavvyInfo);
        setLocation(listingData.location);
        setSelectedFulfillment(listingData.selectedFulfillment);
        setListerHandle(listingData.listerHandle);
        setExternalUrl(listingData.externalUrl);
        setSource(listingData.source);
        setFilePaths(listingData.filePaths);
        setAuctionInfo(listingData.auctionInfo);
        setListedAt(listingData.listedAt);

        toast({
          title: 'Restore successful',
          description: 'Your saved draft has been restored.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } catch (error) {
        toast({
          title: 'Restore failed',
          description:
            'We couldn’t restore your saved draft. You can continue with a new listing — your changes will be saved automatically.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        Sentry.captureException(error);
        console.error('Error loading listing data from local storage:', error);
      }
    }
  };

  const restoreDataFromLocalStorage = () => {
    if (!selectedCategory) {
      return;
    }
    const key = buildLocalStorageKey();
    if (key) {
      loadListingDataFromLocalStorage(key);
      setShowLoadDataConfirmBanner(false);
    }
  };

  useEffect(() => {
    // If Banner is shown, do not save data so that we don't overwrite user's changes
    if (showLoadDataConfirmBanner) {
      return;
    }
    // Save data to local storage on change
    if (
      selectedCategory &&
      (title ||
        description ||
        Object.keys(listingAttributeValues).length > 0 ||
        variants.length > 0 ||
        selectedFulfillment ||
        listerHandle ||
        externalUrl ||
        source ||
        filePaths.length > 0 ||
        (auctionInfo && (auctionInfo.start || auctionInfo.end)))
    ) {
      const key = buildLocalStorageKey();
      if (key) {
        setLocalStorageKey(key);
        dumpListingDataInLocalStorage(key);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showLoadDataConfirmBanner,
    selectedCategory,
    title,
    description,
    listingAttributeValues,
    currency,
    variants,
    currentVariant,
    shippingInfo,
    parcelInfo,
    keysavvyInfo,
    location,
    selectedFulfillment,
    listerHandle,
    externalUrl,
    source,
    filePaths,
    auctionInfo,
    listedAt,
  ]);

  useEffect(() => {
    // Local storage dump is only for listing creation
    if (writingMode !== 'Add') {
      return;
    }
    // Load data if any from local storage
    const key = buildLocalStorageKey();
    // This means the user changed the category, we need to cleanup previous data and
    // set new for current category
    if (localStorageKey && key && key !== '' && key !== localStorageKey) {
      localStorage.removeItem(localStorageKey);
      dumpListingDataInLocalStorage(key);
      setLocalStorageKey(key);
    } else if (!localStorageKey && key && key !== '') {
      try {
        const storedData = localStorage.getItem(key);
        if (storedData) {
          const listingData = JSON.parse(storedData);
          const currentTime = new Date().getTime();
          if (listingData.expirationTime && currentTime > listingData.expirationTime) {
            // Data has expired, remove it from Local Storage
            localStorage.removeItem(key);
            return;
          } else {
            // Show confirm to load data
            setShowLoadDataConfirmBanner(true);
          }
        } else {
          setShowLoadDataConfirmBanner(false);
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error('Error loading listing data from local storage:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketplace, selectedCategory, writingMode]);

  if (renderState === 'loading') {
    return (
      <Flex pt={40} justifyContent="center">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <>
      {isPayToList && writingMode === 'Add' && (
        <Container
          maxW={CONTAINER_MAX_WIDTH_1}
          my={{ base: 4, md: 6, lg: 8 }}
          pt={{ base: 2, md: 4, lg: 8 }}
          border="1px"
          borderColor="gray.200"
          borderRadius={theme.border_radius.border_radius_2}
        >
          <VStack spacing={2} textAlign="center">
            <Heading as="h1" fontSize="4xl">
              {!isEmpty(payToListParent?.create_listing_name)
                ? payToListParent?.create_listing_name
                : payToListParent?.name ?? ''}
            </Heading>
            <Text fontSize="lg" color={'gray.500'}>
              {payToListParent?.seller_description ?? ''}
            </Text>
          </VStack>

          {renderPayToListCustomContent()}

          <Flex
            width="full"
            direction={{ base: 'column', md: 'row' }}
            gap={6}
            p={{ base: 2, md: 4, lg: 8 }}
          >
            {payToListChildren
              .sort((a, b) => {
                // sort first by position, then by id
                let comparisonRes = a.position - b.position;
                if (comparisonRes === 0) {
                  comparisonRes = a.id - b.id;
                }
                return comparisonRes;
              })
              .map((elem) => {
                return (
                  <PriceCard
                    childCategory={elem}
                    key={elem.id}
                    handleChangeCategory={setSelectedCategory}
                    selectedCategory={selectedCategory}
                    handleSelectedChild={handleSelectedChild}
                    parentCategory={payToListParent}
                  />
                );
              })}
          </Flex>
          {payToListParent?.functional?.pay_to_list?.pay_to_list_text && (
            <Flex width="full" direction={{ base: 'column' }} gap={4} p={{ base: 2, md: 4, lg: 8 }}>
              <Text fontWeight="500" fontSize="xl" textAlign="left">
                FAQ
              </Text>
              <Text>{payToListParent?.functional?.pay_to_list?.pay_to_list_text}</Text>
            </Flex>
          )}
        </Container>
      )}

      {hasPermission ? (
        <Container maxW={CONTAINER_MAX_WIDTH_2} ref={containerRef}>
          {!authLoading && !isAuthenticated && writingMode === 'Edit' ? (
            <Container maxW={CONTAINER_MAX_WIDTH_2} my={{ base: 4, md: 6, lg: 8 }}>
              <Text
                fontSize={{ base: 'xl', lg: '2xl' }}
                fontWeight={600}
                mb={{ base: 2, md: 4, lg: 6 }}
              >
                Edit listing
              </Text>
              <LoginPrompt
                message="to edit a listing."
                showWithModalOpen={false}
                sendToAccountInfo={false} // Don't send to account info to keep them in the create listing flow
              />
            </Container>
          ) : (
            <form
              onSubmit={handleSubmit}
              style={{
                display:
                  !isPayToList || selectedChild?.name || writingMode === 'Edit' ? 'block' : 'none',
              }}
            >
              <Container maxW={CONTAINER_MAX_WIDTH_2} my={{ base: 4, md: 6, lg: 8 }}>
                <Heading
                  fontSize={{ base: 'xl', lg: '2xl' }}
                  fontWeight={600}
                  mb={{ base: 2, md: 4, lg: 6 }}
                >
                  {writingMode === 'Add' ? 'Add new listing' : 'Edit listing'}
                </Heading>
                {renderCustomContent()}
                {showLoadDataConfirmBanner && (
                  <Alert status="info" mb={4} variant="left-accent">
                    <AlertIcon />
                    <Flex direction="column">
                      <AlertTitle>We found saved changes from your last session.</AlertTitle>
                      <AlertDescription>
                        Would you like to continue where you left off?
                      </AlertDescription>
                    </Flex>
                    <Flex ml="auto" gap={2}>
                      <Button
                        variant="outline"
                        colorScheme="blue"
                        onClick={() => setShowLoadDataConfirmBanner(false)}
                      >
                        Discard
                      </Button>
                      <Button colorScheme="blue" onClick={restoreDataFromLocalStorage}>
                        Restore
                      </Button>
                    </Flex>
                  </Alert>
                )}
                <Flex
                  width="full"
                  direction="column"
                  p={{ base: 2, md: 4, lg: 8 }}
                  border="1px"
                  borderColor="gray.200"
                  borderRadius={theme.border_radius.border_radius_2}
                >
                  {/* Basic Info */}
                  {!authLoading && !isAuthenticated && writingMode === 'Add' && (
                    <EmailCollector
                      email={email}
                      setEmail={setEmail}
                      ref={emailCollectorRef}
                      showLabel
                    />
                  )}
                  <Grid width="full" column={2} gap={{ base: 3, md: 5 }} autoColumns="1fr">
                    <GridItem colSpan={2}>
                      <Flex justifyContent={'space-between'}>
                        <Text fontSize="md" fontWeight={600}>
                          Basic Info
                        </Text>
                        <Flex gap={4}>
                          {writingMode === 'Add' &&
                            ((!isSingleListingImportAvailable && (isAdmin || isLomaAdmin)) ||
                              isSingleListingImportAvailable) && (
                              <ListingImport
                                categoryId={
                                  typeof currentCategoryId === 'string'
                                    ? parseInt(currentCategoryId, 10)
                                    : currentCategoryId
                                }
                              />
                            )}
                          {writingMode !== 'Add' && isNoAuctionOrEnded && (
                            <ISoldThis
                              listing={listing}
                              marketplace={marketplace}
                              mode="form"
                              afterUpdate={updateListing}
                            />
                          )}
                        </Flex>
                      </Flex>
                    </GridItem>
                    <GridItem colSpan={{ base: 2, md: 1 }}>
                      <Input
                        type="text"
                        name="title"
                        placeholder={defaultOverrides['listing_title'] ?? 'Title'}
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        isRequired
                        data-test="title-input"
                      />
                    </GridItem>
                    <GridItem colSpan={{ base: 2, md: 1 }}>
                      {!isPayToList && (
                        <CategorySelector
                          marketplaceId={marketplace?.id}
                          selectedCategoryId={String(currentCategoryId ?? '')}
                          onCategorySelect={setSelectedCategory}
                          disabled={categorySelectorDisabled}
                          disabledReason="You cannot change the category after you have paid to list."
                          isRequired
                          mode={isAdmin ? 'listing-create-admin' : 'listing-create'}
                          isPayToList={isPayToList}
                          handlePayToList={setIsPayToList}
                        />
                      )}
                    </GridItem>

                    {/* Listing Attributes Fields */}
                    <AttributesList
                      attributes={selectedCategory?.attributes}
                      values={listingAttributeValues}
                      onChange={handleAttributeChange}
                    />

                    <GridItem colSpan={2}>
                      <FormControl isRequired>
                        <FormLabel>
                          {defaultOverrides['listing_description'] ?? 'Description'}
                        </FormLabel>
                        <Textarea
                          name="description"
                          placeholder={
                            'Enter your ' +
                            (
                              defaultOverrides['listing_description'] ?? 'description'
                            ).toLowerCase() +
                            ' here'
                          }
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          data-test="description-input"
                        />
                      </FormControl>
                    </GridItem>

                    {/* Listing Currency */}
                    {(selectedCategory?.functional?.currencies_allowed?.length ?? 0) > 1 && (
                      <GridItem colSpan={2}>
                        <Flex direction="column">
                          <FormLabel>Currency</FormLabel>
                          <Select
                            name="currency"
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                          >
                            {selectedCategory?.functional?.currencies_allowed.map((currency) => (
                              <option key={currency} value={currency}>
                                {currency.toUpperCase()}
                              </option>
                            ))}
                          </Select>
                        </Flex>
                      </GridItem>
                    )}

                    {/* Single Variant Form */}
                    {categoryTypeHasPrice(selectedCategory?.type) && (
                      <>
                        {!isMultiVariant && (
                          <Stack>
                            <VariantForm
                              selectedCategory={selectedCategory}
                              currentVariant={currentVariant}
                              setCurrentVariant={setCurrentVariant}
                              isSingleVariant={selectedCategory?.type !== CATEGORY_TYPE_MARKETPLACE}
                            />
                          </Stack>
                        )}

                        {selectedCategory?.type === CATEGORY_TYPE_MARKETPLACE && (
                          <GridItem colSpan={2}>
                            <Text
                              paddingTop={2}
                              fontStyle={'italic'}
                              color={'orange.400'}
                              fontSize={'small'}
                            >
                              A marketplace fee of{' '}
                              <b>
                                ${(Number(currentVariant?.offered_price) * takeRate).toFixed(2)}
                              </b>{' '}
                              will be subtracted from your payout after this sells.
                            </Text>
                            {!paymentInfoFound && (
                              <Text
                                paddingTop={1}
                                fontStyle={'italic'}
                                color={'gray.500'}
                                fontSize={'small'}
                              >
                                Be sure to enter your payout details (e.g. your Venmo/Cash App info)
                                in{' '}
                                <b>
                                  <NextLink href="/account/info">Account Info</NextLink>
                                </b>
                                .
                              </Text>
                            )}
                            <Text
                              paddingTop={1}
                              fontStyle={'italic'}
                              color={'gray.500'}
                              fontSize={'small'}
                            >
                              See{' '}
                              <b>
                                <NextLink href="/transaction_terms">Transaction Terms</NextLink>
                              </b>{' '}
                              for more information.
                            </Text>
                          </GridItem>
                        )}
                      </>
                    )}

                    <GridItem colSpan={2} textAlign="right">
                      {listingId ? (
                        <Button
                          isLoading={submitInfoLoading}
                          colorScheme="gray"
                          variant="solid"
                          px={12}
                          data-action="saveInfo"
                          type="submit"
                          onClick={handleSubmit}
                          isDisabled={
                            imageUploading ||
                            (!isAuthenticated && writingMode === 'Edit') ||
                            disallowUserEdits
                          }
                        >
                          {saveInfoButtonText}
                        </Button>
                      ) : (
                        <Button
                          isLoading={submitDraftLoading}
                          colorScheme="gray"
                          variant="solid"
                          px={12}
                          data-action="saveDraft"
                          type="submit"
                          onClick={handleSubmit}
                          isDisabled={
                            imageUploading ||
                            (!isAuthenticated && writingMode === 'Edit') ||
                            disallowUserEdits
                          }
                        >
                          {saveInfoButtonText}
                        </Button>
                      )}
                    </GridItem>
                  </Grid>
                  <Divider my={{ base: 4, lg: 6 }} />

                  {/* Multi-Variant Form */}
                  {categoryTypeHasPrice(selectedCategory?.type) && (
                    <>
                      {isMultiVariant ? (
                        <>
                          <Grid width="full" column={3} gap={{ base: 3, md: 5 }} autoColumns="1fr">
                            <GridItem colSpan={3}>
                              <Text fontSize="md" fontWeight={600}>
                                Item Variations
                              </Text>
                            </GridItem>
                            <VariantForm
                              selectedCategory={selectedCategory}
                              currentVariant={currentVariant}
                              setCurrentVariant={setCurrentVariant}
                              isSingleVariant={false}
                            />
                            <GridItem colSpan={3} textAlign="right">
                              <Button
                                colorScheme="gray"
                                variant="solid"
                                px={12}
                                onClick={handleAddVariantSubmit}
                                data-test="add-variation-btn"
                              >
                                Add Variation
                              </Button>
                            </GridItem>
                          </Grid>
                          <Divider my={{ base: 4, lg: 6 }} />
                          {variants?.length === 0 && (
                            <Alert status="info">
                              <AlertIcon /> Add at least one variation
                            </Alert>
                          )}
                          <Accordion
                            allowToggle
                            mb={{ base: 4, lg: 6 }}
                            onChange={(expandedIndex) => {
                              if (expandedIndex == -1) {
                                setCurrentVariant(null);
                              }
                            }}
                          >
                            {variants
                              ?.sort((item1, item2) => {
                                if (item1.id && item2.id) {
                                  return item1.id - item2.id;
                                }
                                return 0;
                              })
                              .map((variant) => (
                                <AccordionItem
                                  key={variant?.id}
                                  border="1px"
                                  borderColor="gray.300"
                                  borderRadius={theme.border_radius.border_radius_2}
                                  mb={4}
                                >
                                  {({ isExpanded }) => (
                                    <>
                                      <h2>
                                        <AccordionButton
                                          _expanded={{
                                            borderTopRadius: theme.border_radius.border_radius_2,
                                            borderBottom: '1px',
                                            borderColor: 'gray.300',
                                            bgColor: 'gray.100',
                                          }}
                                          onClick={() => {
                                            if (!isExpanded) {
                                              setCurrentVariant(variant);
                                            }
                                          }}
                                        >
                                          <Box as="span" flex="1" textAlign="left">
                                            {variant.offered_price && (
                                              <span>${variant.offered_price}</span>
                                            )}
                                            {Object.keys(variant.variant_attribute_values || {})
                                              .length > 0 && ' - '}

                                            {Object.entries(variant.variant_attribute_values || {})
                                              .map(([attributeId, attributeValue]) => {
                                                const attribute = selectedCategory?.attributes.find(
                                                  (attr) => attr.id === parseInt(attributeId),
                                                );
                                                return attribute
                                                  ? `${attribute.id}: ${attributeValue}`
                                                  : null;
                                              })
                                              .filter(Boolean)
                                              .join(', ')}
                                            <span>&nbsp;-&nbsp;Qty: {variant.quantity}</span>
                                          </Box>
                                          {!isExpanded && (
                                            <Icon
                                              as={FiEdit2}
                                              textColor="primary.400"
                                              fontSize="xl"
                                            />
                                          )}
                                        </AccordionButton>
                                      </h2>
                                      <AccordionPanel
                                        py={4}
                                        borderRadius={theme.border_radius.border_radius_2}
                                        _expanded={{ border: '1px', borderColor: 'gray.300' }}
                                      >
                                        <Grid
                                          width="full"
                                          column={3}
                                          gap={{ base: 3, md: 5 }}
                                          autoColumns="1fr"
                                        >
                                          <VariantForm
                                            selectedCategory={selectedCategory}
                                            currentVariant={currentVariant}
                                            setCurrentVariant={setCurrentVariant}
                                            isSingleVariant={false}
                                          />
                                          <GridItem colSpan={3} textAlign="right">
                                            <Flex
                                              alignItems="center"
                                              justifyContent="flex-end"
                                              flexWrap="wrap"
                                              gap={4}
                                            >
                                              <Button
                                                colorScheme="red"
                                                variant="outline"
                                                onClick={deleteVariant}
                                              >
                                                Delete
                                              </Button>

                                              <Button
                                                colorScheme="gray"
                                                variant="solid"
                                                onClick={() => handleEditVariantSubmit(variant?.id)}
                                              >
                                                Save Edit
                                              </Button>
                                            </Flex>
                                          </GridItem>
                                        </Grid>
                                      </AccordionPanel>
                                    </>
                                  )}
                                </AccordionItem>
                              ))}
                          </Accordion>
                          <Divider my={{ base: 4, lg: 6 }} />
                        </>
                      ) : null}
                    </>
                  )}

                  {/* Fulfillment Option Types */}
                  {/* TODO(Sean): Allow selecting multiple fulfillment option types */}
                  {selectedCategory?.type === CATEGORY_TYPE_MARKETPLACE && (
                    <>
                      <Grid width="full" column={2} gap={{ base: 3, md: 5 }} autoColumns="1fr">
                        <GridItem colSpan={2}>
                          <Text fontSize="md" fontWeight={600}>
                            Shipping & Fulfillment Options
                          </Text>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <Select
                            name="fulfillment_options"
                            placeholder="Select Option"
                            value={selectedFulfillment?.id || ''}
                            onChange={(e) => {
                              const selectedFulfillmentId = e.target.value;
                              const selectedFulfillment = selectedCategory?.fulfillment_types?.find(
                                (option) => option.id === parseInt(selectedFulfillmentId),
                              );
                              setSelectedFulfillment(selectedFulfillment || null);
                            }}
                            data-test="fullfilment-input"
                          >
                            {selectedCategory?.fulfillment_types?.map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.name}
                              </option>
                            ))}
                          </Select>
                        </GridItem>
                      </Grid>
                      {selectedFulfillment?.name !== 'KeySavvy' && (
                        <Divider my={{ base: 4, lg: 6 }} />
                      )}
                    </>
                  )}

                  {selectedFulfillment && selectedFulfillment.name !== 'KeySavvy' && (
                    <>
                      <Box pt={4} pl={1}>
                        <HStack>
                          <Text fontSize="sm" fontWeight={500} pb={2}>
                            {selectedFulfillment.name}
                          </Text>
                          {selectedFulfillment.price_type === 'dynamic_api' && (
                            <Text fontSize="sm" fontWeight={500} pb={2}>
                              &nbsp;-&nbsp;dynamic pricing
                            </Text>
                          )}
                          {selectedFulfillment?.price_type === 'fixed_at_type' && (
                            <Text fontSize="sm" fontWeight={500} pb={2}>
                              &nbsp;-&nbsp;${selectedFulfillment.price}
                            </Text>
                          )}
                          {selectedFulfillment?.price_type === 'fixed_at_option_type' && (
                            <Text fontSize="sm" fontWeight={500} pb={2}>
                              &nbsp;-&nbsp;${selectedFulfillment.price}
                            </Text>
                          )}
                          {selectedFulfillment?.price_type === 'fixed_at_option' && (
                            <GridItem colSpan={1}>
                              <FormControl>
                                <InputGroup>
                                  <InputLeftAddon color={BLACK}>$</InputLeftAddon>
                                  <NumberInput
                                    mb={4}
                                    name="shippingPrice"
                                    placeholder="Price"
                                    precision={2}
                                    min={0}
                                    value={fulfillmentPrice || ''}
                                    onChange={(v) => setFulfillmentPrice(v)}
                                    isRequired
                                  >
                                    <NumberInputField borderLeftRadius={0} />
                                  </NumberInput>
                                </InputGroup>
                              </FormControl>
                            </GridItem>
                          )}
                        </HStack>
                        <Text fontSize="sm" fontWeight={300}>
                          {selectedFulfillment?.description}
                        </Text>
                      </Box>
                      <Divider my={{ base: 4, lg: 6 }} />
                    </>
                  )}

                  {/* Shipping */}
                  {selectedFulfillment &&
                    selectedFulfillment.name !== 'KeySavvy' &&
                    selectedFulfillment?.price_type === 'dynamic_api' && (
                      <>
                        <Grid width="full" column={3} gap={{ base: 3, md: 5 }} autoColumns="1fr">
                          <GridItem colSpan={3}>
                            <Text fontSize="md" fontWeight={600}>
                              Shipping
                            </Text>
                          </GridItem>
                          <GridItem colSpan={3}>
                            <AddressInput
                              addressValue={shippingInfo}
                              setAddressValue={setShippingInfo}
                              formType="shipping"
                              skipRequired={!!externalUrl}
                            />
                          </GridItem>
                          <GridItem colSpan={3} textAlign="right">
                            <Button
                              isLoading={submitLoading}
                              colorScheme="gray"
                              variant="solid"
                              px={12}
                              data-action="saveInfo"
                              type="submit"
                              isDisabled={!isAuthenticated}
                            >
                              Save Address
                            </Button>
                          </GridItem>
                        </Grid>

                        <Divider my={{ base: 4, lg: 6 }} />

                        {/* Parcel Info */}
                        <ParcelInfoForm parcelInfo={parcelInfo} setParcelInfo={setParcelInfo} />

                        <Divider my={{ base: 4, lg: 6 }} />
                      </>
                    )}

                  {selectedFulfillment && selectedFulfillment.name === 'KeySavvy' && (
                    <>
                      <KeysavvyInfoForm
                        keysavvyInfo={keysavvyInfo || DEFAULT_KEYSAVVY_INFO}
                        setKeysavvyInfo={setKeysavvyInfo}
                      />
                    </>
                  )}

                  {/* Images */}
                  <Grid width="full" column={2} gap={{ base: 3, md: 5 }} autoColumns="1fr">
                    <GridItem colSpan={2}>
                      <Text fontSize="md" fontWeight={600}>
                        Photos / Videos
                      </Text>
                    </GridItem>
                    <GridItem colSpan={2} data-test="listing-images">
                      <ListingImageEditor
                        maxFiles={selectedCategory?.functional?.multiple_listing_photos ?? 24}
                        filePaths={filePaths}
                        setFilePaths={setFilePaths}
                        setImageUploading={setImageUploading}
                        showFeaturedBadge
                      />
                    </GridItem>
                  </Grid>
                  <Divider my={{ base: 4, lg: 6 }} />

                  {/* Location */}
                  <ListingLocationForm
                    hasLocation={hasLocation}
                    setHasLocation={setHasLocation}
                    location={location}
                    setLocation={setLocation}
                    shippingInfo={shippingInfo}
                    sameAsShippingInfo={sameAsShippingInfo}
                    setSameAsShippingInfo={setSameAsShippingInfo}
                    requiresLocation={isLocationRequired}
                    categoryType={selectedCategory?.type}
                    addressAttribute={addressAttribute}
                  />

                  {selectedCategory?.type === 'auction' && (
                    <ListingAuctionForm auctionInfo={auctionInfo} setAuctionInfo={setAuctionInfo} />
                  )}

                  {/* Featured */}
                  {isStaffOrAdmin && (
                    <ListingFeaturedForm isFeatured={isFeatured} setIsFeatured={setIsFeatured} />
                  )}

                  {(isLomaAdmin || isStaffOrAdmin) && (
                    <>
                      <Divider my={{ base: 4, lg: 6 }} />
                      <Grid
                        width="full"
                        column={2}
                        gap={{ base: 3, md: 5 }}
                        autoColumns="1fr"
                        mb={8}
                      >
                        <GridItem colSpan={2}>
                          <Text fontSize="md" fontWeight={600}>
                            Admin
                          </Text>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <FormControl>
                            <FormLabel>External URL</FormLabel>
                            <Input
                              type="text"
                              name="external_url"
                              placeholder="External URL"
                              value={externalUrl || ''}
                              onChange={(e) => setExternalUrl(e.target.value)}
                            />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <FormControl>
                            <FormLabel>Source</FormLabel>
                            <Input
                              type="text"
                              name="source"
                              placeholder="Source"
                              value={source || ''}
                              onChange={(e) => setSource(e.target.value)}
                            />
                          </FormControl>
                        </GridItem>
                        <GridItem colSpan={2}>
                          <FormControl>
                            <FormLabel>Lister Handle (optional)</FormLabel>
                            <Input
                              type="text"
                              name="lister_handle"
                              placeholder={`${capitalizeFirstLetter(source as string)} Seller`}
                              value={listerHandle || ''}
                              onChange={(e) => setListerHandle(e.target.value)}
                            />
                          </FormControl>
                        </GridItem>

                        {/* Listed At */}
                        <GridItem colSpan={2}>
                          <FormControl>
                            <FormLabel>Listed Date</FormLabel>
                            <Input
                              type="text"
                              name="listed_at"
                              placeholder="Listed Date"
                              value={listedAt || ''}
                              onChange={(e) => setListedAt(e.target.value)}
                            />
                          </FormControl>
                        </GridItem>
                      </Grid>
                    </>
                  )}

                  {/* Sibling Categories to Upgrade */}
                  {siblingsToUpdate.length > 0 && isPayToListChild && (
                    <>
                      <Divider my={{ base: 4, lg: 6 }} />
                      <Box mb={{ base: 8 }}>
                        <Text mb={{ base: 2, lg: 4 }}>Reach more buyers:</Text>
                        <Flex wrap={'wrap'} alignItems={'start'} gap={{ base: 8 }} w={'full'}>
                          {siblingsToUpdate.map((cat) => (
                            <SiblingToUpdateCard
                              category={cat}
                              key={cat.id}
                              handleSelectedChild={(cat) => {
                                setSelectedChild(cat);
                              }}
                              handleChangeCategory={setSelectedCategory}
                            />
                          ))}
                        </Flex>
                      </Box>
                    </>
                  )}

                  {/* Submit */}
                  <ListingFormSubmit
                    selectedCategory={selectedCategory}
                    listing={listing}
                    listingId={listingId}
                    submitLoading={submitLoading}
                    setSubmitLoading={setSubmitLoading}
                    submitDraftLoading={submitDraftLoading}
                    handleSubmit={handleSubmit}
                    writingMode={writingMode}
                    isDisabled={imageUploading || disallowUserEdits}
                    onCancelSubscription={onCancelSubscription}
                  />
                </Flex>
              </Container>
            </form>
          )}
        </Container>
      ) : (
        <Container maxW={CONTAINER_MAX_WIDTH_2} ref={containerRef} mt={10}>
          <ErrorMessageBox>
            {errorMessage}
            &nbsp;You can add roles to your account from the&nbsp;
            <Button variant="link" onClick={() => router.push('/account/roles')}>
              <Text as="span" textDecoration="underline">
                Marketplace Roles
              </Text>
            </Button>
            &nbsp;page.
          </ErrorMessageBox>
        </Container>
      )}

      <AttributeValueCaptureModal
        isOpen={showPayoutCaptureModal}
        onClose={() => setShowPayoutCaptureModal(false)}
        attributeIds={showPayoutCaptureModalAttributeIds}
        onSave={checkPaymentInfo}
        alertText="You must enter your payout information before your listing can go live."
        requireAllAttributes
        createAttributeValues
      />
      <AttributeValueCaptureModal
        isOpen={showAttributeCaptureModal}
        onClose={() => setShowAttributeCaptureModal(false)}
        attributeIds={attributeIdsForCaptureModal}
        onAttributeChange={handleAttributeChange}
        attributeValues={attributeIdsForCaptureModal.reduce((acc, id) => {
          if (listingAttributeValues[id]) {
            acc[id] = listingAttributeValues[id];
          }
          return acc;
        }, {} as FlatAttributeValuesType)}
        resetAttributeValues={resetCaptureModaltAttributeValues}
        onSave={handleSubmit}
      />
    </>
  );
};

export default ListingForm;
